import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Alert,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
  GetApp as GetAppIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  fetchConsignments,
  setFilters,
  setPage,
} from "../../store/slices/consignmentSlice";
import {
  exportConsignmentsCsv,
  generateBookingSlips,
  generatePrintList,
  printConsignment,
  cancelConsignment,
  importConsignments,
} from "../../services/consignmentApi";
import { useTheme } from "../../contexts/ThemeContext";
import { clientApi, Client } from "../../services/clientApi";
import { cityApi, City } from "../../services/cityApi";
import { branchApi, Branch } from "../../services/branchApi";
import ConsignmentView from "./ConsignmentView";
import StatusBadge from "../../components/StatusBadge";
import DateRangePicker from "../../components/DateRangePicker";
import DateTimeRangePicker from "../../components/DateTimeRangePicker";
import Toast from "../../components/Toast";
import useToast from "../../hooks/useToast";
import GenericModal from "../../components/GenericModal";

const Consignments: React.FC = () => {
  const { theme } = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { items, pagination, loading, error, filters } = useSelector(
    (s: RootState) => s.consignments,
  );
  const { user } = useSelector((s: RootState) => s.auth);
  const { toast, showSuccess, showError, hideToast } = useToast();

  const statusOptions = [
    { id: 1, name: "Booked" },
    { id: 2, name: "Ready for Pickup" },
    { id: 3, name: "Picked Up" },
    { id: 4, name: "Arrived at hub" },
    { id: 5, name: "Ready to Ship" },
    { id: 6, name: "Shipped" },
    { id: 7, name: "Arrived at Destination" },
    { id: 8, name: "Assigned to Rider" },
    { id: 9, name: "Delivered" },
    { id: 10, name: "Refused" },
    { id: 11, name: "Re-attempt to Deliver" },
    { id: 12, name: "Pending for RTO" },
    { id: 13, name: "Returned to Origin" },
    { id: 14, name: "Arrived at Origin" },
    { id: 15, name: "Return Ready to Ship" },
    { id: 16, name: "Return Assigned to Rider" },
    { id: 17, name: "Return Shipped" },
    { id: 18, name: "Returned to Consigner" },
    { id: 19, name: "Cancelled" },
    { id: 20, name: "Pending for Pickup" },
    { id: 21, name: "Dispatched for Delivery" },
    { id: 22, name: "Delivery Sheet Checked In" },
    { id: 23, name: "Delivery Sheet Closed" },
    { id: 24, name: "Lost" },
    { id: 25, name: "Short Arrived" },
    { id: 26, name: "Paid" },
  ];

  const [customers, setCustomers] = useState<Client[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewId, setViewId] = useState<number | null>(null);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [selectedConsignment, setSelectedConsignment] = useState<any>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
    errors?: any[];
  } | null>(null);

  const isClient = user && user.type === "CLIENT";
  const isAdmin = user && user.type === "ADMIN";
  const isActiveClient = isClient && user.status === 1;

  useEffect(() => {
    dispatch(fetchConsignments());
  }, [
    dispatch,
    filters.query,
    filters.status,
    filters.booking_type,
    filters.customer_id,
    filters.destination,
    filters.from,
    filters.to,
    filters.page,
    filters.per_page,
    filters.first_mile_node_id,
    filters.current_node_id,
    filters.last_mile_node_id,
    filters.created_from,
    filters.created_to,
    filters.updated_from,
    filters.updated_to,
  ]);

  useEffect(() => {
    (async () => {
      try {
        // Load minimal clients list (first page large page size)
        const resp = await clientApi.getClients({ page: 1, per_page: 100 });
        setCustomers(resp.clients);
      } catch {}
      try {
        const cityResp = await cityApi.getCities({ page: 1, per_page: 200 });
        setCities(cityResp.cities);
      } catch {}
      try {
        const branchResp = await branchApi.getBranches({ per_page: 200 });
        setBranches(branchResp.branches);
      } catch {}
    })();
  }, []);

  const handleOpenView = (id: number) => {
    setViewId(id);
    setViewOpen(true);
  };

  const handleBulkMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setBulkMenuAnchor(event.currentTarget);
  };

  const handleBulkMenuClose = () => {
    setBulkMenuAnchor(null);
  };

  const handleActionMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    consignment: any,
  ) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedConsignment(consignment);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedConsignment(null);
  };

  const handleBulkAction = async (action: string) => {
    handleBulkMenuClose();
    try {
      switch (action) {
        case "bookingSlips":
          await generateBookingSlips(filters);
          break;
        case "printList":
          await generatePrintList(filters);
          break;
        case "downloadCSV":
          await exportConsignmentsCsv(filters);
          break;
      }
    } catch (error) {
      console.error("Bulk action failed:", error);
      // You could add a toast notification here
    }
  };

  const handleIndividualAction = async (action: string) => {
    if (!selectedConsignment) return;

    // Close the menu but don't clear selectedConsignment yet for cancel action
    setActionMenuAnchor(null);

    try {
      switch (action) {
        case "print":
          await printConsignment(selectedConsignment.id);
          setSelectedConsignment(null);
          break;
        case "edit":
          // Edit consignment (only for clients with COD bookings)
          // You could navigate to edit page here
          setSelectedConsignment(null);
          break;
        case "cancel":
          // Don't clear selectedConsignment - we need it for the dialog
          setCancelDialogOpen(true);
          break;
        default:
          setSelectedConsignment(null);
      }
    } catch (error) {
      console.error("Individual action failed:", error);
      setSelectedConsignment(null);
    }
  };

  const handleCancelConsignment = async () => {
    if (selectedConsignment && cancelReason.trim()) {
      try {
        await cancelConsignment(selectedConsignment.id, cancelReason);
        showSuccess(
          `Consignment ${selectedConsignment.consignment_no} has been cancelled`,
          "Cancelled",
        );
        setCancelDialogOpen(false);
        setCancelReason("");
        setSelectedConsignment(null);
        // Refresh the consignments list
        dispatch(fetchConsignments());
      } catch (error: any) {
        console.error("Failed to cancel consignment:", error);
        showError(
          error.message || "Failed to cancel consignment",
          "Cancellation Failed",
        );
      }
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setSelectedConsignment(null);
  };

  const getReasonForConsignment = (consignment: any) => {
    if (consignment.status?.id === 25) {
      // Short Arrived - get reason from tracking
      return consignment.reason || "Short received";
    } else if ([10, 11].includes(consignment.status?.id)) {
      // Refused or Re-attempt - get reason from delivery sheet
      return consignment.reason || "Delivery issue";
    }
    return "";
  };

  const handleDateRangeChange = (from: string, to: string) => {
    dispatch(setFilters({ from, to }));
  };

  const handleDateRangeClear = () => {
    dispatch(setFilters({ from: "", to: "" }));
  };

  const handleImportFile = async () => {
    if (importFile) {
      setImportLoading(true);
      setImportResult(null);
      try {
        const result = await importConsignments(importFile);
        setImportResult({
          success: true,
          message: result.message || "Import completed successfully",
          errors: result.data?.errors || [],
        });
        setImportFile(null);
        // Refresh the consignments list
        dispatch(fetchConsignments());
      } catch (error: any) {
        setImportResult({
          success: false,
          message: error.message || "Failed to import consignments",
        });
      } finally {
        setImportLoading(false);
      }
    }
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleDownloadSampleCsv = () => {
    const API_BASE_URL =
      process.env.REACT_APP_BACKEND_URL || "https://api.citycourier.org/api/v1";
    window.open(`${API_BASE_URL}/consignments/sample-csv`, "_blank");
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Consignments ({pagination.total || 0})
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isActiveClient && (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
          )}
          {Array.isArray(items) && items.length > 0 && (
            <Button
              variant="outlined"
              endIcon={<MoreVertIcon />}
              onClick={handleBulkMenuOpen}
            >
              Actions
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ mb: 2, color: theme.palette.text.primary }}
          >
            <FilterIcon sx={{ mr: 1, verticalAlign: "middle" }} /> Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Search Consignment #"
                value={filters.query || ""}
                onChange={(e) =>
                  dispatch(setFilters({ query: e.target.value }))
                }
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => dispatch(fetchConsignments())}>
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status ?? ""}
                  onChange={(e) =>
                    dispatch(setFilters({ status: e.target.value as any }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {statusOptions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Booking Type</InputLabel>
                <Select
                  label="Booking Type"
                  value={filters.booking_type ?? ""}
                  onChange={(e) =>
                    dispatch(
                      setFilters({ booking_type: e.target.value as any }),
                    )
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="COD">COD</MenuItem>
                  <MenuItem value="RETAIL">Retail</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <DateRangePicker
                fromDate={filters.from}
                toDate={filters.to}
                onDateChange={handleDateRangeChange}
                onClear={handleDateRangeClear}
              />
            </Grid>
            {!isClient && (
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    label="Customer"
                    value={filters.customer_id ?? ""}
                    onChange={(e) =>
                      dispatch(
                        setFilters({ customer_id: e.target.value as any }),
                      )
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    {customers.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Destination</InputLabel>
                <Select
                  label="Destination"
                  value={filters.destination ?? ""}
                  onChange={(e) =>
                    dispatch(setFilters({ destination: e.target.value as any }))
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Node Filters */}
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>FM Node</InputLabel>
                <Select
                  label="FM Node"
                  value={filters.first_mile_node_id ?? ""}
                  onChange={(e) =>
                    dispatch(
                      setFilters({ first_mile_node_id: e.target.value as any }),
                    )
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Current Node</InputLabel>
                <Select
                  label="Current Node"
                  value={filters.current_node_id ?? ""}
                  onChange={(e) =>
                    dispatch(
                      setFilters({ current_node_id: e.target.value as any }),
                    )
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>LM Node</InputLabel>
                <Select
                  label="LM Node"
                  value={filters.last_mile_node_id ?? ""}
                  onChange={(e) =>
                    dispatch(
                      setFilters({ last_mile_node_id: e.target.value as any }),
                    )
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* DateTime Filters */}
            <Grid size={{ xs: 12, md: 3 }}>
              <DateTimeRangePicker
                label="Created Time"
                fromDateTime={filters.created_from}
                toDateTime={filters.created_to}
                onDateTimeChange={(from, to) =>
                  dispatch(setFilters({ created_from: from, created_to: to }))
                }
                onClear={() =>
                  dispatch(setFilters({ created_from: "", created_to: "" }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <DateTimeRangePicker
                label="Updated Time"
                fromDateTime={filters.updated_from}
                toDateTime={filters.updated_to}
                onDateTimeChange={(from, to) =>
                  dispatch(setFilters({ updated_from: from, updated_to: to }))
                }
                onClear={() =>
                  dispatch(setFilters({ updated_from: "", updated_to: "" }))
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            sx={{
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: "20px",
                background: `linear-gradient(to left, ${theme.palette.background.paper}, transparent)`,
                pointerEvents: "none",
                display: { xs: "block", md: "none" },
              },
            }}
          >
            <TableContainer
              sx={{
                width: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorX: "contain",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#333" : "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#666" : "#c1c1c1",
                  borderRadius: "4px",
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 800, tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "120px", minWidth: "120px" }}>
                      Consignment #
                    </TableCell>
                    <TableCell sx={{ width: "100px", minWidth: "100px" }}>
                      Date
                    </TableCell>
                    {!isClient && (
                      <TableCell sx={{ width: "120px", minWidth: "120px" }}>
                        Customer
                      </TableCell>
                    )}
                    <TableCell sx={{ width: "200px", minWidth: "200px" }}>
                      Consignee
                    </TableCell>
                    <TableCell sx={{ width: "100px", minWidth: "100px" }}>
                      Service
                    </TableCell>
                    <TableCell sx={{ width: "80px", minWidth: "80px" }}>
                      Order
                    </TableCell>
                    <TableCell sx={{ width: "80px", minWidth: "80px" }}>
                      COD
                    </TableCell>
                    {!isClient && (
                      <TableCell sx={{ width: "100px", minWidth: "100px" }}>
                        Failed Attempts
                      </TableCell>
                    )}
                    <TableCell sx={{ width: "120px", minWidth: "120px" }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ width: "80px", minWidth: "80px" }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(items) && items.length > 0 ? (
                    items.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Button
                            variant="text"
                            onClick={() => handleOpenView(row.id)}
                          >
                            {row.consignment_no}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {new Date(row.created_at).toLocaleDateString()}
                        </TableCell>
                        {!isClient && (
                          <TableCell>{row.user?.name || "-"}</TableCell>
                        )}
                        <TableCell
                          sx={{
                            fontSize: "0.875rem",
                            lineHeight: 1.3,
                            wordBreak: "break-word",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <div>
                            <strong>Name: </strong>
                            {row.consignee?.name}
                          </div>
                          <div>
                            <strong>Phone: </strong>
                            {row.consignee?.phone}
                            {row.consignee?.phone2
                              ? `, ${row.consignee.phone2}`
                              : ""}
                          </div>
                          <div>
                            <strong>Address: </strong>
                            {row.consignee?.address}
                          </div>
                          <div>
                            <strong>City: </strong>
                            {row.destinationCity?.name}
                          </div>
                        </TableCell>
                        <TableCell>{row.service_type}</TableCell>
                        <TableCell>{row.booking_type}</TableCell>
                        <TableCell>{row.cod_amount}</TableCell>
                        {!isClient && (
                          <TableCell>{row.failed_attempts || 0}</TableCell>
                        )}
                        <TableCell>
                          <StatusBadge status={row.status} />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionMenuOpen(e, row)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : loading ? (
                    <TableRow>
                      <TableCell colSpan={isClient ? 7 : 9} align="center">
                        Loading consignments...
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isClient ? 7 : 9} align="center">
                        {Array.isArray(items)
                          ? "No consignments found"
                          : "Error loading consignments"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <TablePagination
            component="div"
            count={pagination.total}
            page={(pagination.current_page || 1) - 1}
            rowsPerPage={pagination.per_page}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, p) => dispatch(setPage(p + 1))}
            onRowsPerPageChange={(e) =>
              dispatch(
                setFilters({ per_page: parseInt(e.target.value, 10), page: 1 }),
              )
            }
          />
        </CardContent>
      </Card>

      <ConsignmentView
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        consignmentId={viewId}
      />

      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkMenuAnchor}
        open={Boolean(bulkMenuAnchor)}
        onClose={handleBulkMenuClose}
      >
        <MenuItem onClick={() => handleBulkAction("bookingSlips")}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Booking Slips</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction("printList")}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print List</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction("downloadCSV")}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download CSV</ListItemText>
        </MenuItem>
      </Menu>

      {/* Individual Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        {(isAdmin || selectedConsignment?.status?.id !== 19) && (
          <MenuItem onClick={() => handleIndividualAction("print")}>
            <ListItemIcon>
              <PrintIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Print</ListItemText>
          </MenuItem>
        )}
        {isClient &&
          selectedConsignment?.status?.id === 1 &&
          selectedConsignment?.booking_type === "COD" && (
            <MenuItem onClick={() => handleIndividualAction("edit")}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
        {isClient &&
          selectedConsignment?.status?.id === 1 &&
          selectedConsignment?.booking_type === "COD" && (
            <MenuItem onClick={() => handleIndividualAction("cancel")}>
              <ListItemIcon>
                <CancelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cancel</ListItemText>
            </MenuItem>
          )}
      </Menu>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Consignments</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {importResult && (
              <Alert
                severity={importResult.success ? "success" : "error"}
                sx={{ mb: 2 }}
                onClose={() => setImportResult(null)}
              >
                {importResult.message}
              </Alert>
            )}

            {!importResult?.success && (
              <>
                <TextField
                  fullWidth
                  type="file"
                  inputProps={{ accept: ".csv,.txt" }}
                  label="Upload CSV File"
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    setImportFile(file || null);
                  }}
                  disabled={importLoading}
                />
                <Typography
                  variant="body2"
                  sx={{ mt: 2, color: "text.secondary" }}
                >
                  Maximum 150 consignments per file. Use the sample file to see
                  the required format.
                </Typography>
                <Button
                  startIcon={<GetAppIcon />}
                  size="small"
                  onClick={handleDownloadSampleCsv}
                  sx={{ mt: 1 }}
                >
                  Download Sample File
                </Button>
              </>
            )}

            {importResult?.errors && importResult.errors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                  Failed rows ({importResult.errors.length}):
                </Typography>
                <Box
                  sx={{
                    maxHeight: 300,
                    overflow: "auto",
                    bgcolor: "background.default",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  {importResult.errors.map((err: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 1,
                        p: 1,
                        bgcolor: "error.light",
                        borderRadius: 1,
                        opacity: 0.9,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        Row {index + 1}: {err.ConsigneeName || "Unknown"} -{" "}
                        {err.Destination || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="error.dark">
                        {err.ERRORS}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>
            {importResult?.success ? "Close" : "Cancel"}
          </Button>
          {!importResult?.success && (
            <Button
              variant="contained"
              onClick={handleImportFile}
              disabled={!importFile || importLoading}
            >
              {importLoading ? "Importing..." : "Import"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Consignment Modal */}
      <GenericModal
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        title="Cancel Consignment"
        subtitle={selectedConsignment?.consignment_no}
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={handleCancelDialogClose}
              variant="outlined"
              sx={{
                color: theme.palette.text.secondary,
                borderColor: theme.palette.divider,
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelConsignment}
              disabled={!cancelReason.trim()}
            >
              Cancel Consignment
            </Button>
          </>
        }
      >
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Reason for cancellation"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          required
          placeholder="Please provide a reason for cancelling this consignment"
        />
      </GenericModal>
      <Toast
        open={toast.open}
        message={toast.message}
        title={toast.title}
        severity={toast.severity}
        onClose={hideToast}
      />
    </Box>
  );
};

export default Consignments;
